import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { bakeColorGradeForEngine, evaluateKeyframes } from '@cinegrid/core';
import { shouldOutputPreviewAudio } from '../../engine/audioGate';
import { Compositor, effectAmounts } from '../../engine/compositor';
import {
  advanceLivePlayhead,
  getLivePlayhead,
  syncLivePlayhead,
} from '../../engine/livePlayhead';
import { getPreviewAudioEngine } from '../../engine/previewAudio';
import { createSeekController, type SeekController } from '../../engine/seekController';
import { resolveAssetUrl } from '../../media/mediaStore';
import { useEditorStore } from '../../state/editorStore';
import { IconButton, PanelShell } from '../controls/PanelShell';
import { ViewerTransformOverlay } from './ViewerTransformOverlay';
import { ViewerTransport } from './ViewerTransport';

type ViewerZoom = 'fit' | number;

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

/** Live preview: HTMLVideoElement decode + WebGL2 grade/composite. */
export function ViewerPanel() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const compositorRef = useRef<Compositor | null>(null);
  const videoCache = useRef<Map<string, HTMLVideoElement>>(new Map());
  const seekCache = useRef<Map<string, SeekController>>(new Map());
  const imageCache = useRef<Map<string, HTMLImageElement>>(new Map());
  const imageLoading = useRef<Set<string>>(new Set());
  const imageRetryAt = useRef<Map<string, number>>(new Map());
  const texCache = useRef<Map<string, WebGLTexture>>(new Map());
  const textCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const playRafRef = useRef<number>(0);
  const drawRafRef = useRef<number>(0);
  const needsDraw = useRef(true);
  const [viewerZoom, setViewerZoom] = useState<ViewerZoom>('fit');
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });

  const project = useEditorStore((s) => s.project);
  const playhead = useEditorStore((s) => s.playhead);
  const isPlaying = useEditorStore((s) => s.isPlaying);
  const isLeader = useEditorStore((s) => s.isLeader);
  const layoutId = useEditorStore((s) => s.layout.id);
  const connectedTabs = useEditorStore((s) => s.connectedTabs);
  const setPlayhead = useEditorStore((s) => s.setPlayhead);
  const setPlaying = useEditorStore((s) => s.setPlaying);
  const setComposition = useEditorStore((s) => s.setComposition);
  const tool = useEditorStore((s) => s.tool);
  const addTextClip = useEditorStore((s) => s.addTextClip);

  const { width, height } = project.settings;

  const activeVideoAsset = useMemo(() => {
    const clip = project.clips.find(
      (c) =>
        playhead >= c.start &&
        playhead < c.start + c.duration &&
        c.assetId &&
        (c.kind === 'video' || c.kind === 'solid')
    );
    if (!clip?.assetId) return null;
    return project.assets.find((a) => a.id === clip.assetId) ?? null;
  }, [project, playhead]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      compositorRef.current = new Compositor(canvas);
    } catch (err) {
      console.error(err);
    }
    return () => {
      compositorRef.current?.destroy();
      compositorRef.current = null;
      for (const s of seekCache.current.values()) s.destroy();
      seekCache.current.clear();
      for (const v of videoCache.current.values()) {
        v.pause();
        v.src = '';
      }
      videoCache.current.clear();
      imageCache.current.clear();
    };
  }, []);

  // Primary editor always owns audio; Color Suite stays silent
  const outputAudio = shouldOutputPreviewAudio({
    layout: { id: layoutId },
    isLeader,
    connectedTabs,
  });

  // Unlock / resume AudioContext — browsers block audio until a gesture on *this* tab
  useEffect(() => {
    if (!outputAudio) return;
    const unlock = () => {
      const s = useEditorStore.getState();
      getPreviewAudioEngine().sync(s.project, getLivePlayhead(), s.isPlaying);
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    window.addEventListener('focus', unlock);
    document.addEventListener('visibilitychange', unlock);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
      window.removeEventListener('focus', unlock);
      document.removeEventListener('visibilitychange', unlock);
    };
  }, [outputAudio]);

  // Drive audio from the live clock during play (store playhead is throttled)
  useEffect(() => {
    if (!outputAudio) {
      getPreviewAudioEngine().sync(project, playhead, false);
      return;
    }
    if (!isPlaying) {
      getPreviewAudioEngine().sync(project, playhead, false);
      return;
    }
    let raf = 0;
    const tick = () => {
      const s = useEditorStore.getState();
      getPreviewAudioEngine().sync(s.project, getLivePlayhead(), true);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [project, playhead, isPlaying, outputAudio]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (cr) setStageSize({ w: cr.width, h: cr.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Invalidate draw when picture-affecting state changes (playhead polled in RAF while playing)
  useEffect(() => {
    needsDraw.current = true;
  }, [project, isPlaying, width, height]);

  useEffect(() => {
    if (!isPlaying) needsDraw.current = true;
  }, [playhead, isPlaying]);

  // Prefetch every visual asset so dual-viewer tabs resolve logos / stills / clips
  useEffect(() => {
    let cancelled = false;
    const bump = () => {
      if (!cancelled) needsDraw.current = true;
    };

    const loadImageAsset = (asset: (typeof project.assets)[number]) => {
      if (imageCache.current.has(asset.id) || imageLoading.current.has(asset.id)) return;
      imageLoading.current.add(asset.id);
      void resolveAssetUrl(asset)
        .then((url) => loadImage(url))
        .then((img) => {
          imageCache.current.set(asset.id, img);
          imageRetryAt.current.delete(asset.id);
          bump();
        })
        .catch(() => {
          imageRetryAt.current.set(asset.id, performance.now() + 1500);
        })
        .finally(() => {
          imageLoading.current.delete(asset.id);
        });
    };

    for (const asset of project.assets) {
      if (asset.kind === 'audio' || asset.kind === 'adjustment' || asset.kind === 'solid') {
        continue;
      }
      if (asset.kind === 'image') {
        loadImageAsset(asset);
        continue;
      }
      void resolveAssetUrl(asset)
        .then(() => bump())
        .catch(() => undefined);
    }

    const onMediaReady = (ev: Event) => {
      const assetId = (ev as CustomEvent<{ assetId: string }>).detail?.assetId;
      if (!assetId) return;
      imageRetryAt.current.delete(assetId);
      const asset = useEditorStore
        .getState()
        .project.assets.find((a) => a.id === assetId);
      if (asset?.kind === 'image') loadImageAsset(asset);
      else bump();
    };
    window.addEventListener('cinegrid-media-ready', onMediaReady);

    return () => {
      cancelled = true;
      window.removeEventListener('cinegrid-media-ready', onMediaReady);
    };
  }, [project.assets]);

  // Keep live playhead aligned when scrubbing / remote sync while paused
  useEffect(() => {
    if (!isPlaying) syncLivePlayhead(playhead);
  }, [playhead, isPlaying]);

  // Playback clock — leader advances; Color Suite interpolates playhead for UI + proxy frames
  useEffect(() => {
    if (!isPlaying) return;

    const drivesClock = () => {
      const s = useEditorStore.getState();
      return s.isLeader || s.connectedTabs.length <= 1;
    };

    syncLivePlayhead(useEditorStore.getState().playhead);
    let last = performance.now();

    const tick = (now: number) => {
      const dt = Math.min(0.08, (now - last) / 1000);
      last = now;
      const s = useEditorStore.getState();
      const maxDur = Math.max(
        s.project.settings.duration,
        ...s.project.clips.map((c) => c.start + c.duration),
        1
      );

      if (drivesClock()) {
        const next = getLivePlayhead() + dt;
        if (next >= maxDur) {
          setPlaying(false);
          advanceLivePlayhead(maxDur, setPlayhead, { force: true });
        } else {
          advanceLivePlayhead(next, setPlayhead, { playing: true, minMs: 66 });
          needsDraw.current = true;
        }
      } else {
        // Follower: smooth local playhead between BroadcastChannel packets
        const remote = s.playhead;
        let live = getLivePlayhead() + dt;
        const err = remote - live;
        const viewer2 = s.layout.id === 'viewer-only';
        // Viewer Only free-runs decode — avoid hard snaps that force seeks
        const snap = viewer2 ? 1.25 : 0.45;
        const blend = viewer2 ? 0.06 : 0.15;
        if (Math.abs(err) > snap) live = remote;
        else live += err * blend;
        live = Math.min(maxDur, Math.max(0, live));
        syncLivePlayhead(live);
        needsDraw.current = true;
      }

      playRafRef.current = requestAnimationFrame(tick);
    };

    playRafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(playRafRef.current);
  }, [isPlaying, isLeader, connectedTabs.length, setPlayhead, setPlaying]);

  // After dual playback stops, snap Color Suite viewer to the editor playhead once
  useEffect(() => {
    if (isPlaying) return;
    if (isLeader || connectedTabs.length <= 1) return;
    needsDraw.current = true;
  }, [isPlaying, isLeader, connectedTabs.length, playhead]);

  // Continuous draw loop with coalesced seeks
  useEffect(() => {
    let alive = true;
    let lastDrawAt = 0;
    let lastProxySeekAt = 0;
    const bump = () => {
      needsDraw.current = true;
    };

    const ensureVideo = (asset: { id: string; url: string; name?: string; mimeType?: string }) => {
      let video = videoCache.current.get(asset.id);
      if (!video) {
        video = document.createElement('video');
        video.muted = true;
        video.playsInline = true;
        video.preload = 'auto';
        try {
          (video as HTMLVideoElement & { disableRemotePlayback?: boolean }).disableRemotePlayback =
            true;
        } catch {
          /* ignore */
        }
        videoCache.current.set(asset.id, video);
        void resolveAssetUrl(asset)
          .then((url) => {
            const v = videoCache.current.get(asset.id);
            if (!v) return;
            return new Promise<void>((resolve) => {
              const done = () => {
                v.removeEventListener('loadeddata', done);
                v.removeEventListener('error', done);
                resolve();
              };
              v.addEventListener('loadeddata', done);
              v.addEventListener('error', done);
              v.src = url;
              void v.play().then(() => v.pause()).catch(() => undefined);
              window.setTimeout(done, 3000);
            });
          })
          .catch((err) => console.warn('Media resolve failed', err))
          .finally(() => bump());
      }
      if (!seekCache.current.has(asset.id)) {
        seekCache.current.set(asset.id, createSeekController(video, bump));
      }
      return video;
    };

    /** Follower tab with editor also open. */
    const dualFollowerPreview = (s: ReturnType<typeof useEditorStore.getState>) =>
      !s.isLeader && s.connectedTabs.length > 1;

    /**
     * Viewer Only → full free-run dual preview (smooth second monitor).
     * Color Suite → light proxy (don't fight the editor decoder).
     */
    const previewSize = (
      projW: number,
      projH: number,
      mode: 'solo' | 'viewer2' | 'color-proxy'
    ) => {
      const maxEdge = mode === 'color-proxy' ? 720 : mode === 'viewer2' ? 1920 : 4096;
      const long = Math.max(projW, projH);
      if (long <= maxEdge) return { w: projW, h: projH };
      const scale = maxEdge / long;
      return {
        w: Math.max(2, Math.round((projW * scale) / 2) * 2),
        h: Math.max(2, Math.round((projH * scale) / 2) * 2),
      };
    };

    const drawFrame = (playheadTime: number, playing: boolean) => {
      const comp = compositorRef.current;
      if (!comp) return;

      const state = useEditorStore.getState();
      const proj = state.project;
      const dual = dualFollowerPreview(state);
      const layoutId = state.layout.id;
      const colorPage = layoutId === 'color-suite';
      const viewer2 = dual && (layoutId === 'viewer-only' || !colorPage);
      // Color Suite only: sparse seeks. Viewer Only free-runs like before.
      const proxyPlay = dual && playing && colorPage;
      const sizeMode = !dual
        ? 'solo'
        : colorPage
          ? 'color-proxy'
          : 'viewer2';
      const { w, h } = previewSize(
        proj.settings.width,
        proj.settings.height,
        sizeMode
      );
      comp.resize(w, h);

      const videoTracks = [...proj.tracks]
        .filter((t) => t.kind === 'video' && t.visible)
        .reverse();

      const frameSize = { width: w, height: h };
      const jobs: Array<() => void> = [];
      // Viewer2: very loose drift so free-run isn't constantly seek-corrected
      const driftLimit = proxyPlay ? 1.5 : viewer2 ? 2.5 : dual ? 1.5 : 0.85;
      const nowMs = performance.now();
      const doProxySeek = proxyPlay && nowMs - lastProxySeekAt > 80;
      if (doProxySeek) lastProxySeekAt = nowMs;

      for (const track of videoTracks) {
        const clip = proj.clips.find(
          (c) =>
            c.trackId === track.id &&
            playheadTime >= c.start &&
            playheadTime < c.start + c.duration
        );
        if (!clip) continue;

        let transform = { ...clip.properties.transform };
        let color = bakeColorGradeForEngine(clip.properties.color);
        const local = playheadTime - clip.start;
        const kfs = clip.properties.keyframes;
        for (const prop of [
          'opacity',
          'positionX',
          'positionY',
          'scaleX',
          'scaleY',
          'rotation',
        ] as const) {
          const v = evaluateKeyframes(kfs, prop, local);
          if (v != null) (transform as Record<string, number>)[prop] = v;
        }
        const exposure = evaluateKeyframes(kfs, 'exposure', local);
        const saturation = evaluateKeyframes(kfs, 'saturation', local);
        const hue = evaluateKeyframes(kfs, 'hue', local);
        if (exposure != null) color.exposure = exposure;
        if (saturation != null) color.saturation = saturation;
        if (hue != null) color.hue = hue;

        let dissolve = 0;
        if (clip.transitionOut && local > clip.duration - clip.transitionOut.duration) {
          dissolve =
            (local - (clip.duration - clip.transitionOut.duration)) /
            clip.transitionOut.duration;
        }

        if (clip.kind === 'adjustment') {
          const fx = effectAmounts(clip.properties.effects);
          const xf = transform;
          jobs.push(() => {
            comp.applyAdjustment({ color, effects: fx, transform: xf });
          });
          continue;
        }

        if (clip.kind === 'solid') {
          const assetSolid = clip.assetId
            ? proj.assets.find((a) => a.id === clip.assetId)
            : undefined;
          const hex =
            clip.properties.solidColor ||
            assetSolid?.solidColor ||
            '#808080';
          const texKey = `solid-${hex}`;
          let tex = texCache.current.get(texKey);
          if (!tex) {
            tex = comp.createSolidTexture(hex);
            texCache.current.set(texKey, tex);
          }
          const texRef = tex;
          const fx = effectAmounts(clip.properties.effects);
          jobs.push(() => {
            comp.drawLayer({
              texture: texRef,
              transform,
              color,
              crossDissolve: dissolve,
              frameSize,
              effects: fx,
            });
          });
          continue;
        }

        if (clip.kind === 'text' && clip.properties.text) {
          if (!textCanvasRef.current) textCanvasRef.current = document.createElement('canvas');
          const tc = textCanvasRef.current;
          tc.width = w;
          tc.height = h;
          const ctx = tc.getContext('2d');
          if (ctx) {
            ctx.clearRect(0, 0, w, h);
            const t = clip.properties.text;
            ctx.font = `${t.fontWeight} ${t.fontSize}px ${t.fontFamily}`;
            ctx.fillStyle = t.color;
            ctx.textAlign = t.align;
            ctx.textBaseline = 'middle';
            const x = t.align === 'left' ? 80 : t.align === 'right' ? w - 80 : w / 2;
            if (t.strokeWidth > 0) {
              ctx.strokeStyle = t.strokeColor;
              ctx.lineWidth = t.strokeWidth;
              ctx.strokeText(t.content, x, h / 2);
            }
            ctx.fillText(t.content, x, h / 2);
          }
          const texKey = `text-${clip.id}`;
          let tex = texCache.current.get(texKey);
          if (!tex) {
            tex = comp.createTextureFromCanvas(tc);
            texCache.current.set(texKey, tex);
          } else {
            comp.gl.bindTexture(comp.gl.TEXTURE_2D, tex);
            comp.gl.texImage2D(
              comp.gl.TEXTURE_2D,
              0,
              comp.gl.RGBA,
              comp.gl.RGBA,
              comp.gl.UNSIGNED_BYTE,
              tc
            );
          }
          const texRef = tex;
          const fx = effectAmounts(clip.properties.effects);
          jobs.push(() => {
            comp.drawLayer({
              texture: texRef,
              transform,
              color,
              crossDissolve: dissolve,
              frameSize,
              effects: fx,
            });
          });
          continue;
        }

        if (!clip.assetId) continue;
        const asset = proj.assets.find((a) => a.id === clip.assetId);
        if (!asset || asset.kind === 'audio') continue;

        if (asset.kind === 'image') {
          let img = imageCache.current.get(asset.id);
          if (!img) {
            const now = performance.now();
            const retryAt = imageRetryAt.current.get(asset.id) ?? 0;
            if (!imageLoading.current.has(asset.id) && now >= retryAt) {
              imageLoading.current.add(asset.id);
              void resolveAssetUrl(asset)
                .then((url) => loadImage(url))
                .then((loaded) => {
                  imageCache.current.set(asset.id, loaded);
                  imageRetryAt.current.delete(asset.id);
                  bump();
                })
                .catch((err) => {
                  console.warn('Image resolve failed', err);
                  imageRetryAt.current.set(asset.id, now + 1500);
                })
                .finally(() => {
                  imageLoading.current.delete(asset.id);
                });
            }
            continue;
          }
          const texKey = `img-${asset.id}`;
          let tex = texCache.current.get(texKey);
          if (!tex) {
            tex = comp.createTextureFromCanvas(img);
            texCache.current.set(texKey, tex);
          }
          const texRef = tex;
          const fx = effectAmounts(clip.properties.effects);
          jobs.push(() => {
            comp.drawLayer({
              texture: texRef,
              transform,
              color,
              crossDissolve: dissolve,
              sourceSize: {
                width: img.naturalWidth || asset.width || w,
                height: img.naturalHeight || asset.height || h,
              },
              frameSize,
              effects: fx,
            });
          });
          continue;
        }

        const video = ensureVideo(asset);
        const sourceTime = clip.inPoint + local;
        const seeker = seekCache.current.get(asset.id)!;
        const fx = effectAmounts(clip.properties.effects);

        if (proxyPlay) {
          // Never free-run on Color Suite during dual play — sparse seeks keep picture moving
          if (!video.paused) video.pause();
          if (doProxySeek && Math.abs(video.currentTime - sourceTime) > 0.04) {
            seeker.request(sourceTime);
          }
        } else if (playing) {
          if (video.paused) {
            if (Math.abs(video.currentTime - sourceTime) > 0.05) {
              try {
                video.currentTime = Math.max(0, sourceTime);
              } catch {
                /* ignore */
              }
            }
            void video.play().catch(() => undefined);
          } else if (Math.abs(video.currentTime - sourceTime) > driftLimit) {
            // Soft resync — avoid thrashing the decoder on dual Viewer Only
            if (viewer2) {
              try {
                video.currentTime = Math.max(0, sourceTime);
              } catch {
                seeker.request(sourceTime);
              }
            } else {
              seeker.request(sourceTime);
            }
          }
        } else {
          if (!video.paused) video.pause();
          if (viewer2 || dual) {
            if (Math.abs(video.currentTime - sourceTime) > 0.08) {
              seeker.request(sourceTime);
            }
          } else {
            seeker.request(sourceTime);
          }
        }

        // While playing, keep uploading frames even mid-seek so playhead never outruns picture
        if (video.readyState < 2) {
          const existing = texCache.current.get(asset.id);
          if (existing) {
            const srcW = video.videoWidth || asset.width || w;
            const srcH = video.videoHeight || asset.height || h;
            const fx = effectAmounts(clip.properties.effects);
            jobs.push(() => {
              comp.drawLayer({
                texture: existing,
                transform,
                color,
                crossDissolve: dissolve,
                sourceSize: { width: srcW, height: srcH },
                frameSize,
                effects: fx,
              });
            });
          }
          continue;
        }

        if (!playing && video.seeking) {
          const existing = texCache.current.get(asset.id);
          if (existing) {
            const srcW = video.videoWidth || asset.width || w;
            const srcH = video.videoHeight || asset.height || h;
            const fx = effectAmounts(clip.properties.effects);
            jobs.push(() => {
              comp.drawLayer({
                texture: existing,
                transform,
                color,
                crossDissolve: dissolve,
                sourceSize: { width: srcW, height: srcH },
                frameSize,
                effects: fx,
              });
            });
          }
          continue;
        }

        let tex = texCache.current.get(asset.id);
        if (!tex) {
          tex = comp.createTextureFromVideo(video);
          texCache.current.set(asset.id, tex);
        } else {
          comp.updateTextureFromVideo(tex, video);
        }

        const srcW = video.videoWidth || asset.width || w;
        const srcH = video.videoHeight || asset.height || h;
        const texRef = tex;
        jobs.push(() => {
          comp.drawLayer({
            texture: texRef,
            transform,
            color,
            crossDissolve: dissolve,
            sourceSize: { width: srcW, height: srcH },
            frameSize,
            effects: fx,
          });
        });
      }

      comp.beginFrame(w, h);
      for (const job of jobs) job();
      comp.endFrame();
    };

    const loop = () => {
      if (!alive) return;
      const s = useEditorStore.getState();
      const dual = dualFollowerPreview(s);
      const now = performance.now();
      const ph = s.isPlaying ? getLivePlayhead() : s.playhead;
      const layoutId = s.layout.id;
      // Color Suite: ~12fps proxy. Viewer Only: full RAF (smooth dual monitor).
      const minFrameMs =
        dual && s.isPlaying && layoutId === 'color-suite'
          ? 80
          : dual && layoutId === 'color-suite'
            ? 33
            : 0;

      if (s.isPlaying || needsDraw.current) {
        if (now - lastDrawAt >= minFrameMs) {
          needsDraw.current = false;
          lastDrawAt = now;
          drawFrame(ph, s.isPlaying);
        }
      }
      drawRafRef.current = requestAnimationFrame(loop);
    };
    drawRafRef.current = requestAnimationFrame(loop);

    return () => {
      alive = false;
      cancelAnimationFrame(drawRafRef.current);
    };
  }, []);

  const fitSize = useMemo(() => {
    if (stageSize.w <= 0 || stageSize.h <= 0) return null;
    const frameAspect = width / height;
    const stageAspect = stageSize.w / stageSize.h;
    if (frameAspect > stageAspect) {
      return { w: stageSize.w, h: stageSize.w / frameAspect };
    }
    return { w: stageSize.h * frameAspect, h: stageSize.h };
  }, [stageSize, width, height]);

  const displayStyle = useMemo(() => {
    const aspect = `${width} / ${height}`;
    if (viewerZoom === 'fit' || stageSize.w <= 0 || stageSize.h <= 0) {
      return {
        aspectRatio: aspect,
        width: '100%',
        height: '100%',
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain' as const,
      };
    }
    const scale = viewerZoom / 100;
    return {
      aspectRatio: aspect,
      width: Math.round(width * scale),
      height: Math.round(height * scale),
      maxWidth: 'none',
      maxHeight: 'none',
      objectFit: 'contain' as const,
    };
  }, [viewerZoom, width, height, stageSize]);

  const canvasStyle =
    viewerZoom === 'fit' && fitSize
      ? { width: fitSize.w, height: fitSize.h }
      : displayStyle;

  const zoomLabel = viewerZoom === 'fit' ? 'Fit' : `${viewerZoom}%`;

  const matchSource = () => {
    if (!activeVideoAsset?.width || !activeVideoAsset?.height) return;
    setComposition({
      width: activeVideoAsset.width,
      height: activeVideoAsset.height,
    });
    setViewerZoom('fit');
  };

  const onPreviewPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (e.button !== 0) return;
    if (tool !== 'text') return;
    e.preventDefault();
    e.stopPropagation();

    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const nx = (e.clientX - rect.left) / rect.width;
    const ny = (e.clientY - rect.top) / rect.height;
    // Transform offset is relative to center (0,0)
    const positionX = Math.max(-1, Math.min(1, nx - 0.5));
    const positionY = Math.max(-1, Math.min(1, ny - 0.5));

    const videos = project.tracks.filter((t) => t.kind === 'video' && t.visible && !t.locked);
    const track =
      videos.find((t) => /^v2$/i.test(t.name.trim())) ||
      videos[0] ||
      project.tracks.find((t) => t.kind === 'video');
    if (!track) return;

    addTextClip(track.id, playhead, { positionX, positionY });
  };

  return (
    <div
      id="cinegrid-viewer"
      className="flex h-full min-h-0 flex-col bg-panel [:fullscreen]:h-screen [:fullscreen]:w-screen"
    >
    <PanelShell
      title={
        !isLeader && connectedTabs.length > 1 && isPlaying
          ? layoutId === 'color-suite'
            ? 'Player · proxy follow'
            : 'Player · dual'
          : 'Player'
      }
      actions={
        <div className="flex items-center gap-0.5">
          <IconButton
            title="Zoom out"
            onClick={() =>
              setViewerZoom((z) => {
                const cur = z === 'fit' ? 100 : z;
                return Math.max(10, Math.round(cur / 1.25));
              })
            }
          >
            −
          </IconButton>
          <button
            type="button"
            title="Toggle fit / 100%"
            className="timecode min-w-[2.75rem] px-1 text-2xs text-text-low hover:text-accent"
            onClick={() => setViewerZoom((z) => (z === 'fit' ? 100 : 'fit'))}
          >
            {zoomLabel}
          </button>
          <IconButton
            title="Zoom in"
            onClick={() =>
              setViewerZoom((z) => {
                const cur = z === 'fit' ? 100 : z;
                return Math.min(400, Math.round(cur * 1.25));
              })
            }
          >
            +
          </IconButton>
          <IconButton title="Fit in viewer" active={viewerZoom === 'fit'} onClick={() => setViewerZoom('fit')}>
            Fit
          </IconButton>
          <IconButton
            title="Fullscreen (F)"
            onClick={() => {
              const el = document.getElementById('cinegrid-viewer');
              if (!el) return;
              if (document.fullscreenElement === el) void document.exitFullscreen();
              else void el.requestFullscreen();
            }}
          >
            ⛶
          </IconButton>
          {activeVideoAsset &&
            (activeVideoAsset.width !== width || activeVideoAsset.height !== height) && (
              <IconButton
                title={`Match composition to source (${activeVideoAsset.width}×${activeVideoAsset.height})`}
                onClick={matchSource}
              >
                Match
              </IconButton>
            )}
        </div>
      }
    >
      <div className="flex h-full min-h-0 flex-col">
        <div
          ref={stageRef}
          className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-[var(--cg-viewer-letterbox)] p-3"
        >
          <div className="relative shrink-0" style={canvasStyle}>
            <canvas
              ref={canvasRef}
              className={`absolute inset-0 h-full w-full rounded-lg bg-black shadow-[0_8px_32px_rgba(0,0,0,0.35)] ${tool === 'text' ? 'cursor-text' : ''}`}
              onPointerDown={onPreviewPointerDown}
            />
            <ViewerTransformOverlay disabled={tool === 'text'} />
          </div>
        </div>
        <ViewerTransport />
      </div>
    </PanelShell>
    </div>
  );
}
